import { useState } from 'react';
import { useDispatch } from 'dva';
import { message } from 'antd';
import { useIntl } from 'umi';

//TODO
const usePhotoUpload = () => {
  const [uploading, setUploading] = useState(false);
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  // Function to create a canvas with the transformed and cropped image
  const processImageWithTransforms = async (file, transformations = {}) => {
    const {
      rotation = 0,
      position = { x: 0, y: 0 },
      cropSize = 400,
      previewContainerSize = 320,
    } = transformations;

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          // Create canvas for processing
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Set canvas size to square crop size
          canvas.width = cropSize;
          canvas.height = cropSize;

          // Calculate how the image fits in the preview container with object-fit: cover
          const containerSize = previewContainerSize;

          // object-fit: cover: scale to cover the entire container
          const coverScale = Math.max(containerSize / img.width, containerSize / img.height);
          const scaledWidth = img.width * coverScale;
          const scaledHeight = img.height * coverScale;

          // Calculate scale factor from preview to final canvas
          const scaleFactor = cropSize / containerSize;

          // Save context
          ctx.save();

          // Clear canvas with white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, cropSize, cropSize);

          // Replicate CSS behavior exactly: transform: translate(x, y) rotate(deg)
          // CSS applies transforms in this order:
          // 1. First translate (moves the element)
          // 2. Then rotate (rotates around the NEW position)

          // Step 1: Move to center of canvas (where image naturally sits)
          const naturalCenterX = cropSize / 2;
          const naturalCenterY = cropSize / 2;
          ctx.translate(naturalCenterX, naturalCenterY);

          // Step 2: Apply CSS translate transformation (scaled to canvas)
          ctx.translate(position.x * scaleFactor, position.y * scaleFactor);

          // Step 3: Apply CSS rotate transformation
          if (rotation !== 0) {
            ctx.rotate((rotation * Math.PI) / 180);
          }

          // Step 4: Calculate final image dimensions
          const finalImageWidth = scaledWidth * scaleFactor;
          const finalImageHeight = scaledHeight * scaleFactor;

          // Step 5: Draw image centered on current transformation point
          ctx.drawImage(
            img,
            -finalImageWidth / 2,
            -finalImageHeight / 2,
            finalImageWidth,
            finalImageHeight,
          );

          // Restore context
          ctx.restore();

          // Convert canvas to base64
          const base64 = canvas.toDataURL('image/png').split(',')[1];
          resolve(base64);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = reject;

      if (file instanceof File) {
        const reader = new FileReader();
        reader.onload = (e) => {
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        // If it's already a URL (current photo)
        img.crossOrigin = 'anonymous';
        img.src = file;
      }
    });
  };

  const handlePhotoUpload = async (fileOrUrl, transformations = {}) => {
    if (!fileOrUrl) {
      message.error(formatMessage({ id: 'global.image.upload.no.file' }));
      return false;
    }

    try {
      setUploading(true);

      // Process image with transformations and crop to square
      const base64Content = await processImageWithTransforms(fileOrUrl, transformations);

      // Upload photo using the existing model action
      const result = await dispatch({
        type: 'user/updateProfilePhoto',
        payload: {
          content: base64Content,
        },
      });

      // If we get here without throwing an error, the upload was successful
      message.success(formatMessage({ id: 'global.profile.photo.upload.success' }));

      return true;
    } catch (error) {
      message.error(formatMessage({ id: 'global.profile.photo.upload.error' }));
      return false;
    } finally {
      setUploading(false);
    }
  };

  return {
    uploading,
    handlePhotoUpload,
  };
};

export { usePhotoUpload };
